import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from './baseApi';
import { fetchBaseQuery } from '@reduxjs/toolkit/query';
import { RootState } from '../store';

interface SensorPosition {
  sensorId: number;
  x: number;
  y: number;
}

interface PlanData {
  siteId: number;
  siteDescription: string;
  imageBuffer: string;
  scale?: number;
  sensorPositions?: SensorPosition[];
}

interface GetPlanResponse {
  siteId: number;
  siteDescription: string;
  imageBuffer: string;
}

interface PlanUploadRequest {
  siteId: number;
  siteDescription: string;
  imageBuffer: string;
  scale: number;
  sensorPositions: Array<{
    sensorId: number;
    x: number;
    y: number;
  }>;
}

export const planApi = createApi({
  reducerPath: 'planApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_URL,
    credentials: 'include',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      const user = (getState() as RootState).auth.user;
      
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      
      // Set only the required headers
      headers.set('Content-Type', 'application/json');
      headers.set('x-company-id', user?.companyID?.toString() || '');
      headers.set('x-company-name', user?.companyName || '');
      
      return headers;
    },
  }),
  tagTypes: ['Plan'],
  endpoints: (builder) => ({
    getPlan: builder.query<PlanData, number>({
      query: (siteId) => ({
        url: `service3/sites/${siteId}/plan`,
        method: 'GET',
      }),
      providesTags: ['Plan'],
    }),
    uploadPlan: builder.mutation<void, PlanUploadRequest>({
      query: (data) => {
        if (!data) throw new Error('Plan data is required');
        
        // Ensure the body uses camelCase for property names
        const requestBody = {
          siteId: data.siteId,
          siteDescription: data.siteDescription,
          imageBuffer: data.imageBuffer,
          scale: data.scale,
          sensorPositions: data.sensorPositions.map(pos => ({
            sensorId: pos.sensorId,
            x: pos.x,
            y: pos.y
          }))
        };
        
        return {
          url: `service3/sites/${data.siteId}/plan`,
          method: 'POST',
          body: requestBody,
        };
      },
    }),
    deletePlan: builder.mutation<void, number>({
      query: (siteId) => ({
        url: `/service3/sites/${siteId}/plan`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Plan'],
    }),
  }),
});

export const {
  useGetPlanQuery,
  useUploadPlanMutation,
  useDeletePlanMutation,
} = planApi; 